import axios from "axios";

import {
  UnauthorizedError,
  BadRequestError,
  ForbiddenError,
  NotFoundError,
  BaseError,
  HttpStatusCode,
  NetworkError,
  ExAxiosInstance,
  InternalServerError,
  UnprocessableEntityError
} from "./types";

import { getToken } from "@/helpers/auth-helper";

export const axiosInstance: ExAxiosInstance = Object.assign(
  axios.create({
    baseURL: process.env["VUE_APP_API_ORIGIN"],
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Access-Control-Allow-Origin": "*"
    },
    auth: {
      username: process.env["VUE_APP_API_BASIC_AUTH_USER"],
      password: process.env["VUE_APP_API_BASIC_AUTH_PASS"]
    },
    withCredentials: true,
    responseType: "json",
    timeout: 1000 * 60
  }),
  {
    callbacks: {
      apiErrors: []
    }
  }
);

export const blobRequestInstance: ExAxiosInstance = Object.assign(
  axios.create({
    headers: {
      "X-Requested-With": "XMLHttpRequest",
      "Access-Control-Allow-Origin": "*"
    },
    responseType: "blob",
    timeout: 1000 * 60
  })
);

axiosInstance.interceptors.request.use(
  config => {
    const token = getToken();
    if (token?.authentication_token && config.headers) {
      config.headers["X-AUTH-TOKEN"] = token.authentication_token;
    }

    config.url = config.url && encodeURI(config.url);

    return config;
  },
  error => {
    return Promise.reject(new BaseError(error));
  }
);

axiosInstance.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    let concreteError = new BaseError(error);
    if (!error.response) {
      concreteError = new NetworkError(error);
    } else {
      switch (error.response.status) {
        case HttpStatusCode.Unauthorized:
          concreteError = new UnauthorizedError(error);
          break;
        case HttpStatusCode.BadRequest:
          concreteError = new BadRequestError(error);
          break;
        case HttpStatusCode.Forbidden:
          concreteError = new ForbiddenError(error);
          break;
        case HttpStatusCode.NotFound:
          concreteError = new NotFoundError(error);
          break;
        case HttpStatusCode.UnprocessableEntity:
          concreteError = new UnprocessableEntityError(error);
          break;
        case HttpStatusCode.InternalServerError:
          concreteError = new InternalServerError(error);
          break;
      }
    }
    axiosInstance.callbacks.apiErrors.forEach(callback =>
      callback(concreteError)
    );
    return Promise.reject(concreteError);
  }
);

export * from "./jobs";
export * from "./recruitment_planning";
export * from "./recruitment_review";
export * from "./announcements";
export * from "./company_ranks";
export * from "./agreements";
export * from "./contracts";
